import { useMemo, type FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  InputHelperText,
  InputLabel,
  InputRoot,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mint-lib/tw-components';

import { OptionItem } from '../types/shared.js';

type Props = {
  id: string;
  label?: string;
  a11yLabel: string;
  options: OptionItem[];
  value: string;
  onChange: (option: OptionItem) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  helperText?: string;
  error?: boolean;
};

const BaseSelect: FC<Props> = ({
  id,
  label,
  a11yLabel,
  options,
  value,
  onChange,
  disabled,
  size = 'medium',
  helperText,
  error = false,
}) => {
  const { t } = useTranslation('@myn/approval_v2');

  const mapLabelByValue = useMemo(() => {
    return options.reduce<Record<string, string>>((acc, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {});
  }, [options]);

  const handleChange = (value: string) => {
    onChange({
      value,
      label: mapLabelByValue[value],
    });
  };

  return (
    <InputRoot>
      {label ? (
        <InputLabel htmlFor={id} label={label} disabled={disabled} />
      ) : null}
      <Select disabled={disabled} value={value} onValueChange={handleChange}>
        <SelectTrigger
          id={id}
          size={size}
          color={error ? 'error' : 'default'}
          data-dd-action-name={a11yLabel}
        >
          <SelectValue placeholder={t('Select')} />
        </SelectTrigger>
        <SelectContent>
          {options.map((item, index) => {
            return (
              <SelectItem
                key={item.value}
                value={item.value}
                data-dd-action-name={`Select option ${index + 1}`}
              >
                {item.label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {helperText ? (
        <InputHelperText
          color={error ? 'error' : 'default'}
          helperText={helperText}
          disabled={disabled}
        />
      ) : null}
    </InputRoot>
  );
};

export default BaseSelect;
